/**
 * Types for Resale Shop API
 * Based on: frontend/docs/RESALE_SANDBOX_TESTS.md
 * Endpoint: POST /shop-resale
 */

/**
 * Request payload for purchasing a resale ticket
 * @see RESALE_SANDBOX_TESTS.md - TESTE 2 (lines 143-147)
 */
export type ResaleShopRequest = {
  userId: number;
  resaleId: string; // UUID string
  document: string;
  offerId?: string; // FastPass offer UUID, when buyer came through a promotional offer link
  source?: string; // Acquisition channel — e.g. "waitlist" for Caminho 2
  waitlistId?: string; // Waitlist notification UUID (Caminho 2), for conversion tracking
};

/**
 * Success response from POST /shop-resale
 * @see RESALE_SANDBOX_TESTS.md - TESTE 2 (lines 154-160)
 */
export type ResaleShopResponse = {
  responseData: {
    resale: {
      id: number;
      uuid: string;
      saleTicketId: number;
      userId: number;
      status: string;
      saleValue: number;
      originalPrice: number;
      processingFee: number;
      fastpassFee: number;
      netAmount: number;
      autoSale: boolean;
      fastpassEnabled: boolean;
      url: string;
      resaleUrl: string;
      canDeactivate: boolean;
      activatedAt: string;
      deactivatedAt: string | null;
      soldAt: string | null;
      buyerUserId: number | null;
      createdAt: string;
      updatedAt: string;
      ticket: {
        id: number;
        code: string;
        sequence: string;
        originalPrice: number;
        tax: number;
        guestType: {
          id: number;
          name: string;
          type: string;
        };
      };
      event: {
        id: number;
        title: string;
        from: string | null;
        to: string | null;
        status: string;
        description: string;
        timezone: string;
        link: string;
        type: string;
        producerId: number;
        slug: string;
      };
      isActive: boolean;
      isSold: boolean;
      isDeactivated: boolean;
    };
    saleId: number;
    transactionId: string;
    checkoutUrl: string;
    checkoutId: string;
    postbackUrl: string;
  };
  responseDetails: string;
  responseError: string | null;
  responseStatus: number;
};

/**
 * Error response structure from embedstore API
 */
export type ResaleShopErrorResponse = {
  detail: {
    message: string;
    hint?: string;
    code: ResaleShopErrorCode;
  };
};

/**
 * Error codes documented in RESALE_SANDBOX_TESTS.md
 */
export enum ResaleShopErrorCode {
  /** Resale not found (404) - lines 166-174 */
  RESALE_NOT_FOUND = 'ES0110',

  /** Resale not available for purchase (409) - lines 177-185 */
  RESALE_NOT_AVAILABLE = 'ES0111',

  /** JWT token invalid (401) - lines 188-195 */
  UNAUTHORIZED = 'ES0004',

  /** Legacy API error (500) - lines 198-206 */
  LEGACY_ERROR = 'LG0000',
}

/**
 * Error messages for each error code
 * Maps backend error codes to user-friendly messages
 */
export const RESALE_SHOP_ERROR_MESSAGES: Record<ResaleShopErrorCode, {
  title: string;
  message: string;
  hint?: string;
}> = {
  [ResaleShopErrorCode.RESALE_NOT_FOUND] : {
    title : 'Revenda não encontrada',
    message : 'O ingresso de revenda não foi encontrado ou foi removido.',
    hint : 'Verifique se o link está correto ou procure outro ingresso.',
  },
  [ResaleShopErrorCode.RESALE_NOT_AVAILABLE] : {
    title : 'Revenda indisponível',
    message : 'Este ingresso não está mais disponível para compra.',
    hint : 'O ingresso pode ter sido vendido ou retirado da revenda pelo proprietário.',
  },
  [ResaleShopErrorCode.UNAUTHORIZED] : {
    title : 'Sessão expirada',
    message : 'Sua sessão expirou. Por favor, faça login novamente.',
  },
  [ResaleShopErrorCode.LEGACY_ERROR] : {
    title : 'Erro no servidor',
    message : 'Ocorreu um erro ao processar sua compra. Tente novamente em alguns instantes.',
    hint : 'Se o problema persistir, entre em contato com o suporte.',
  },
};
